// Development Note: To test the Progress component without running optimization,
// add ?dev-progress=true to the URL in development mode

import { t } from "@lingui/macro";
import { CircleNotch, ArrowLeft, Sparkle, Warning } from "@phosphor-icons/react";
import { Button } from "@reactive-resume/ui";
import { useEffect } from "react";
import { useSearchParams } from "react-router";
import { MetricKeyEnum } from "@reactive-resume/schema";
import { useAnalysisContext } from "@/client/contexts/analysis-context";

import {
  MetricsBarChart,
  MandatoryRequirementsChecklist,
  SkillSimilarity,
  RecencyWeighting,
  KeywordInContextIntegrity,
  LanguageClarityQuality,
  AchievementRelevanceIndex,
  DomainIndustryExperience,
  AnalysisErrorBoundary,
  OverallScoreWithOptimize,
  MetricScoreCard,
  AnalysisLoading,
} from "@/client/pages/builder/_components/analysis";

import { Progress } from "@/client/pages/builder/_components/ai-optimize/progress";

import { useResumeStore } from "@/client/stores/resume";
import { useGenerateAnalyze } from "@/client/pages/builder/_hooks/useGenerateAnalyze";

// Analysis generation failed component with retry button
const AnalysisGenerationFailed = ({
  onRetry,
  loading,
  error,
}: {
  onRetry: () => void;
  loading: boolean;
  error: string | null;
}) => (
  <div className="flex flex-col items-center justify-center space-y-2 py-4 text-center">
    <div className="bg-muted rounded-full p-3">
      <Sparkle className="text-muted-foreground size-6" />
    </div>
    <div className="space-y-1">
      <h3 className="text-lg font-bold">{t`Analysis Generation Failed`}</h3>
      <p className="text-muted-foreground text-sm">
        {error ? error : t`Unable to generate resume analysis. Please try again.`}
      </p>
    </div>
    <Button onClick={onRetry} disabled={loading} size="sm" variant="outline">
      {loading ? (
        <>
          <CircleNotch className="mr-2 size-4 animate-spin" />
          {t`Retrying...`}
        </>
      ) : (
        t`Try Again`
      )}
    </Button>
  </div>
);

export const AnalysisSection = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Use the context for all optimization and analysis states
  const {
    isSubscribed,
    events,
    selectedMetric,
    setSelectedMetric,
    handleAIOptimize,
    handleClose,
    isLoadingOptimization,
    isOptimizing,
    optimizationError,
    optimizationData,
    queueStatus,
    isGeneratingQuestions,
  } = useAnalysisContext();

  const {
    isLoading,
    generate: handleGenerateAnalysis,
    retry: handleRetryGeneration,
    generationError,
    hasAttemptedGeneration,
    isGenerating,
    analysisData,
  } = useGenerateAnalyze();

  const resumeId = useResumeStore((state) => state.resume.id);
  const pendingAnalyze = useResumeStore((state) => state.pendingAnalyze);

  // Auto-generate analysis when component mounts if no analysis exists
  useEffect(() => {
    if (!isLoading && !isGenerating && !analysisData && !hasAttemptedGeneration) {
      handleGenerateAnalysis();
    }
  }, [resumeId, analysisData, isLoading, hasAttemptedGeneration, isGenerating]);

  console.log("ANALYZING", {
    isLoading,
    isGenerating,
    pendingAnalyze,
  });

  // Add keyboard navigation support
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && selectedMetric) {
        event.preventDefault();
        setSelectedMetric(null);
      }
    };

    // Only add listener when a metric is selected
    if (selectedMetric) {
      document.addEventListener("keydown", handleKeyDown);
      return () => document.removeEventListener("keydown", handleKeyDown);
    }
  }, [selectedMetric, setSelectedMetric]);

  // Determine if we have analysis to display
  const hasAnalysis = analysisData && analysisData.results;
  const analysesData = hasAnalysis ? analysisData.results : undefined;

  const handleMetricClick = (metric: { id: MetricKeyEnum; title: string }) => {
    // Map the metric ID to its corresponding component
    const getMetricComponent = (id: MetricKeyEnum) => {
      switch (id) {
        case MetricKeyEnum.MRC:
          return <MandatoryRequirementsChecklist analysesData={analysesData!} />;
        case MetricKeyEnum.HSS:
          return (
            <SkillSimilarity analysesData={analysesData!} analysisType="skill" skillType="hard" />
          );
        case MetricKeyEnum.KC:
          return <SkillSimilarity analysesData={analysesData!} analysisType="keyword" />;
        case MetricKeyEnum.RW:
          return <RecencyWeighting analysesData={analysesData!} />;
        case MetricKeyEnum.ARI:
          return <AchievementRelevanceIndex analysesData={analysesData!} />;

        case MetricKeyEnum.KICI:
          return <KeywordInContextIntegrity analysesData={analysesData!} />;

        case MetricKeyEnum.DIE:
          return <DomainIndustryExperience analysesData={analysesData!} />;
        case MetricKeyEnum.LCQ:
          return <LanguageClarityQuality analysesData={analysesData!} />;
        case MetricKeyEnum.SSA:
          return (
            <SkillSimilarity analysesData={analysesData!} analysisType="skill" skillType="soft" />
          );

        default:
          return null;
      }
    };

    setSelectedMetric({
      id: metric.id,
      title: metric.title,
      component: getMetricComponent(metric.id),
    });
  };

  const handleBack = () => {
    setSelectedMetric(null);
  };

  // Metric detail view component
  const MetricDetailView = ({ metric }: { metric: any }) => (
    <div className="w-full max-w-full space-y-4">
      {/* Improved back button with better styling and positioning */}
      <div className="flex items-center justify-start">
        <Button
          size="md"
          variant="outline"
          onClick={handleBack}
          className="hover:bg-secondary/80 focus:ring-primary/20 gap-2 px-4 py-2 focus:ring-2"
        >
          <ArrowLeft className="size-4" />
          {t`Back to Overview`}
        </Button>
      </div>

      {/* Score and reasoning card - shown at the top */}
      {analysesData && <MetricScoreCard metricId={metric.id} analysesData={analysesData} />}

      <div className="w-full max-w-full overflow-hidden rounded-lg border p-2">
        <div className="w-full max-w-full overflow-hidden p-3">{metric.component}</div>
      </div>
    </div>
  );

  // Development flag to show Progress component for testing
  const SHOW_PROGRESS_DEV = searchParams.get("dev-progress") === "true";

  // If optimization is running (subscribed) or dev mode is enabled, show the Progress component
  if (isSubscribed) {
    console.log("⏳ [DEBUG] Rendering Progress component", {
      isSubscribed,
      eventsLength: events.length,
      queueStatus,
      SHOW_PROGRESS_DEV,
    });
    return (
      <Progress
        events={events}
        status={queueStatus}
        onClose={() => {
          console.log("🔒 [DEBUG] Progress - onClose called");
          // Clear dev-progress flag when closing
          if (SHOW_PROGRESS_DEV) {
            console.log("🧪 [DEBUG] Clearing dev-progress flag");
            const newParams = new URLSearchParams(searchParams);
            newParams.delete("dev-progress");
            setSearchParams(newParams);
          }
          handleClose();
        }}
        onRestart={() => {
          console.log("🔄 [DEBUG] Progress - onRestart called");
          handleClose();
          setTimeout(() => handleAIOptimize(), 100);
        }}
        useMockEvents={SHOW_PROGRESS_DEV}
      />
    );
  }

  // Normal view
  console.log("\uD83D\uDCCA [DEBUG] Rendering normal analysis view", {
    hasAnalysis,
    isSubscribed,
    hasOptimizationData: !!optimizationData?.data,
    selectedMetric: selectedMetric?.title || null,
  });

  // Show alert/banner if pendingAnalyze is true
  const PendingAnalyzeAlert = () => (
    <div className="rounded-lg border border-slate-200 bg-slate-100 p-4 dark:border-slate-700 dark:bg-slate-800">
      <div className="flex w-full items-center justify-between">
        <div className="flex items-center gap-x-3">
          <Warning className="text-yellow-500 dark:text-yellow-300" size={26} weight="duotone" />
          <div className="flex flex-col">
            <h4 className="font-semibold text-slate-900 dark:text-slate-100">{t`Outdated Analyse`}</h4>

            <p className="text-sm text-slate-700 dark:text-slate-300">
              {t`Your resume has changed. Click "Reanalyze" to update the analysis.`}
            </p>
          </div>
        </div>
        <Button
          onClick={handleGenerateAnalysis}
          disabled={isGenerating}
          size="sm"
          variant="outline"
        >
          {isGenerating ? (
            <>
              <CircleNotch className="mr-2 size-4 animate-spin" />
              {t`Reanalyzing...`}
            </>
          ) : (
            t`Reanalyze`
          )}
        </Button>
      </div>
    </div>
  );

  if (isLoading || !hasAnalysis || (isGenerating && !hasAttemptedGeneration)) {
    return <AnalysisLoading />;
  }

  if (!hasAnalysis && hasAttemptedGeneration && generationError) {
    return (
      <AnalysisGenerationFailed
        onRetry={handleRetryGeneration}
        loading={isGenerating}
        error={generationError}
      />
    );
  }

  return (
    <section id="analysis" className="flex h-full w-full max-w-full flex-col">
      {/* Pending Analyze Alert */}
      {pendingAnalyze && (
        <div className="mb-4">
          <PendingAnalyzeAlert />
        </div>
      )}

      {/* Fixed header area */}
      <div className="bg-background/95 supports-[backdrop-filter]:bg-background/60 shrink-0 backdrop-blur">
        {hasAnalysis && !selectedMetric && (
          <div className="w-full max-w-full">
            <OverallScoreWithOptimize
              analysesData={analysesData!}
              onOptimize={handleAIOptimize}
              isOptimizing={isOptimizing}
              isGeneratingQuestions={isGeneratingQuestions}
              isLoadingOptimization={isLoadingOptimization}
              optimizationError={optimizationError}
            />
          </div>
        )}
      </div>

      <main className="flex-1 overflow-y-auto">
        <div>
          {hasAnalysis && (
            <AnalysisErrorBoundary
              onRegenerate={handleGenerateAnalysis}
              isGenerating={isGenerating}
            >
              {selectedMetric ? (
                <MetricDetailView metric={selectedMetric} />
              ) : (
                <div className="w-full max-w-full">
                  <MetricsBarChart analysesData={analysesData!} onMetricClick={handleMetricClick} />
                </div>
              )}
            </AnalysisErrorBoundary>
          )}
        </div>
      </main>
    </section>
  );
};
